import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import InstructorLayout from "@/components/InstructorLayout";
import FileUploader, { FileTile } from "@/components/FileUploader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Plus, ChevronRight, Users, Loader2, Trash2, Calendar } from "lucide-react";

export default function InstructorTareas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [cohorts, setCohorts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/instructor/assignments"),
        api.get("/instructor/me/cohorts"),
      ]);
      setItems(r.data.items || []);
      setCohorts(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <InstructorLayout>
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Instructor</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Tareas y entregas</h1>
          <p className="mt-1 text-sm text-gray-400">Crea tareas, adjunta material y revisa entregas de tus alumnos.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="instructor-tareas-create-btn"
                className="inline-flex items-center gap-2 rounded-md bg-[#F26A21] px-4 py-2 text-sm font-semibold text-[#0B0F14] hover:brightness-110">
          <Plus className="w-4 h-4" /> Nueva tarea
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="instructor-tareas-empty">
          <ClipboardCheck className="w-8 h-8 text-gray-500 mx-auto" />
          <p className="mt-3 text-sm text-gray-300">Aún no has publicado tareas.</p>
          <p className="mt-1 text-xs text-gray-500">Crea la primera para tus cohortes activas.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => (
            <Link key={a.id} to={`/app/instructor/tareas/${a.id}`}
                  data-testid={`instructor-tarea-row-${a.id}`}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/[0.08] transition-colors">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-widest text-gray-500">{a.status === "draft" ? "Borrador" : "Publicada"}</p>
                <p className="mt-1 text-sm font-semibold text-white">{a.title}</p>
                {a.due_at && (
                  <p className="mt-0.5 text-[11px] text-gray-400 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Entrega: {new Date(a.due_at).toLocaleString("es-MX")}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400 line-clamp-2">{a.description}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Entregas</p>
                <p className="text-lg font-bold text-white">
                  {a.deliveries?.graded ?? 0}<span className="text-gray-500 text-sm">/{a.deliveries?.total ?? 0}</span>
                </p>
                <ChevronRight className="w-4 h-4 text-gray-500 mx-auto mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAssignmentModal
          cohorts={cohorts}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </InstructorLayout>
  );
}

function CreateAssignmentModal({ cohorts, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [maxScore, setMaxScore] = useState(100);
  const [allowLate, setAllowLate] = useState(true);
  const [cohortId, setCohortId] = useState(cohorts[0]?.id || "");
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const canSubmit = title.trim().length > 3 && cohortId;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const cohort = cohorts.find(c => c.id === cohortId);
      await api.post("/instructor/assignments", {
        title, description,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        max_score: Number(maxScore) || 100,
        allow_late: allowLate,
        cohort_id: cohortId,
        program_id: cohort?.program_id,
        attachments,
        status: "published",
      });
      toast.success("Tarea publicada.");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            data-testid="instructor-tarea-create-modal"
            className="w-full max-w-2xl rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white">Nueva tarea</h2>
        <p className="mt-1 text-xs text-gray-400">Se publicará para todos los alumnos de la cohorte.</p>

        <div className="mt-5 grid gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-300">Cohorte</label>
            <select value={cohortId} onChange={(e) => setCohortId(e.target.value)}
                    data-testid="tarea-cohort-select"
                    className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
              {cohorts.length === 0 && <option value="">— No tienes cohortes —</option>}
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-300">Título</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   data-testid="tarea-title-input"
                   className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                   placeholder="Ej. Análisis financiero corporativo" required />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-300">Instrucciones</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                      data-testid="tarea-desc-input"
                      rows={5}
                      className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                      placeholder="Describe la tarea, criterios de evaluación, formato esperado…" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-300">Fecha límite</label>
              <input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                     data-testid="tarea-due-input"
                     className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-300">Puntaje máx.</label>
              <input type="number" min="1" max="1000" value={maxScore} onChange={(e) => setMaxScore(e.target.value)}
                     data-testid="tarea-maxscore-input"
                     className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
            </div>
            <label className="flex flex-col text-xs font-semibold text-gray-300">
              Permitir tardías
              <input type="checkbox" checked={allowLate} onChange={(e) => setAllowLate(e.target.checked)}
                     data-testid="tarea-late-input"
                     className="mt-1 h-5 w-5 accent-[#F26A21]" />
            </label>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-300">Material de referencia (opcional)</label>
            <div className="mt-1 space-y-2">
              {attachments.map((a) => (
                <FileTile key={a.id} file={a} onRemove={(f) => setAttachments((p) => p.filter(x => x.id !== f.id))} />
              ))}
              <FileUploader kind="assignment_reference"
                            resourceType="assignment"
                            multiple compact
                            onUploaded={(f) => setAttachments((p) => [...p, f])}
                            buttonLabel="Adjuntar material" hint={null} />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-white/20 text-white text-sm">Cancelar</button>
          <button type="submit" disabled={!canSubmit || saving}
                  data-testid="tarea-submit-btn"
                  className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Publicar
          </button>
        </div>
      </form>
    </div>
  );
}
